import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { actions as carFilterActions } from '@redux/modules/carFilter';
import { FilterType } from '@appTypes/urlParamsType';
import { sortByType } from '@appTypes/sortByType';
import { getURLParams } from '@lib/getUrlParams';
import { updateUrlQueryValues } from '../updateUrlQueryValues';
import createElbilguideAPIQuery from './createElbilguideAPIQuery';

// Centralises the URL-write → refreshSearch → fetch-filter-args trio that
// CarSearch / CarFilter / CarSortDropdown each used to inline.
export const useFilterAction = () => {
  const dispatch = useDispatch();

  const fetchFilterArgs = useCallback(() => {
    const queries = createElbilguideAPIQuery(getURLParams());
    dispatch(carFilterActions.getCarArguments.request(queries));
  }, [dispatch]);

  const refresh = useCallback(() => {
    dispatch(carFilterActions.refreshSearch());
  }, [dispatch]);

  const applyQuery = useCallback(
    (value: string) => {
      updateUrlQueryValues.updateQueryValue(value);
      refresh();
      fetchFilterArgs();
    },
    [refresh, fetchFilterArgs],
  );

  const applyFilter = useCallback(
    (filterKey: keyof FilterType, value: string) => {
      updateUrlQueryValues.updateCombinedFilterValue(filterKey, value);
      refresh();
      fetchFilterArgs();
    },
    [refresh, fetchFilterArgs],
  );

  const applySort = useCallback(
    (value: sortByType) => {
      updateUrlQueryValues.updateSortByValue(value);
      refresh();
    },
    [refresh],
  );

  const clearAllFilters = useCallback(() => {
    updateUrlQueryValues.clearAllFilters();
    refresh();
    fetchFilterArgs();
  }, [refresh, fetchFilterArgs]);

  return { applyQuery, applyFilter, applySort, clearAllFilters };
};
